import { DisplayMode } from '../../models/display-mode';
import { RouteListElement } from '../../models/route-list';

/** One stop on the wire. `name` is null — never '' — when no address is known yet. */
interface CarHandoffWaypoint {
  lat: number;
  lng: number;
  name: string | null;
  csid: string | null;
}

/** Mirrors HandoffSnapshot.kt on the Android side. Keep the two in step. */
interface CarHandoffSnapshot {
  v: number;
  rev: number;
  origin: 'web' | 'car';
  mode: string;
  navigating: boolean;
  waypoints: CarHandoffWaypoint[];
  nextWaypointIndex: number;
  skippedChargingStations: string[];
}

const SCHEMA_VERSION = 2;

/**
 * Where the car app leaves the state it wants this page to start from.
 *
 * Written by native into localStorage at document start — before any application script runs —
 * so that by the time anything here reads its own persisted state, the car's version is already
 * the persisted state. Nothing subscribes, nothing races.
 */
const HANDOFF_KEY = 'carHandoff';

const WAYPOINTS_KEY = 'waypoints';
const DISPLAY_MODE_KEY = 'displayMode';
const NAVIGATION_KEY = 'navigation';
const SKIPPED_CHARGERS_KEY = 'skippedChargingStation';

/**
 * Whether this page load began with a snapshot from the car.
 *
 * Module state, so it lasts exactly as long as the load it describes. Read by anything whose
 * start-of-session reset would undo part of the handoff — see MainComponent and the skipped
 * charger list, which is cleared on an ordinary load precisely because it belongs to one trip.
 */
let seededFromCar = false;

export function startedFromCarHandoff(): boolean {
  return seededFromCar;
}

/**
 * Folds a snapshot left by the car app into this app's own persisted state.
 *
 * Called before `bootstrapApplication`, deliberately: `NavigationService` reads
 * [NAVIGATION_KEY] in its constructor and `WaypointService.restore()` reads [WAYPOINTS_KEY] from
 * a route resolver, and the order those two run in is not something this can depend on. Running
 * ahead of the whole injector sidesteps it — every reader then sees one already-settled state.
 *
 * Translating into the app's own keys rather than applying through the services is what keeps
 * this small: the restore path that runs on every ordinary page load does the actual work, and
 * a handoff becomes indistinguishable from the user's own last session. That is also why there
 * is no staleness rule here — restoring a day-old trip is what the app does anyway.
 */
export function consumeCarHandoff(): void {
  let raw: string | null;
  try {
    raw = localStorage.getItem(HANDOFF_KEY);
    // Removed before it is trusted: a payload this version cannot read must not be retried on
    // every subsequent load.
    if (raw !== null) localStorage.removeItem(HANDOFF_KEY);
  } catch {
    // Storage disabled entirely. Nothing to hand over, and nothing that can be done about it.
    return;
  }
  if (!raw) return;

  let snapshot: CarHandoffSnapshot;
  try {
    snapshot = JSON.parse(raw) as CarHandoffSnapshot;
  } catch (e) {
    console.log('[handoff] unparseable snapshot dropped: ' + e);
    return;
  }

  if (snapshot.v > SCHEMA_VERSION) {
    console.log('[handoff] ignored: schema v' + snapshot.v + ' is newer than v' + SCHEMA_VERSION);
    return;
  }
  if (snapshot.origin !== 'car') {
    console.log('[handoff] ignored: origin ' + snapshot.origin + ' is not the car');
    return;
  }

  try {
    // An empty list means the car had no fix and no plan — it is the absence of information, not
    // an instruction to forget the trip, so the waypoints already stored are left alone. A car in
    // range mode still sends its own position, which is one waypoint and does clear the route.
    const waypoints = snapshot.waypoints ?? [];
    if (waypoints.length > 0) {
      const routeList: RouteListElement[] = waypoints.map((w, i) => ({
        display_name: w.name ?? '',
        lat: w.lat,
        lng: w.lng,
        wp: i,
        csid: w.csid ?? undefined,
      }));
      localStorage.setItem(WAYPOINTS_KEY, JSON.stringify(routeList));
    }

    localStorage.setItem(NAVIGATION_KEY, snapshot.navigating ? 'true' : 'false');
    localStorage.setItem(DISPLAY_MODE_KEY, toDisplayMode(snapshot.mode, snapshot.navigating));

    // Written even when empty: a trip with nothing skipped has to overwrite whatever this page
    // last had, or a charger rejected on some earlier trip stays rejected on this one.
    localStorage.setItem(
      SKIPPED_CHARGERS_KEY,
      JSON.stringify(snapshot.skippedChargingStations ?? []),
    );

    seededFromCar = true;

    console.log(
      '[handoff] seeded rev=' + snapshot.rev + ' mode=' + snapshot.mode +
      ' navigating=' + snapshot.navigating + ' waypoints=' + waypoints.length +
      ' skipped=' + (snapshot.skippedChargingStations ?? []).length
    );
  } catch (e) {
    console.log('[handoff] could not write restored state: ' + e);
  }
}

/**
 * The car's mode as one of this app's own.
 *
 * Both sides use the [DisplayMode] strings verbatim, so this is a validation step rather than a
 * translation. NAVIGATION is only honoured while guidance is actually running: the mode outlives
 * a drive in the car's snapshot, and restoring into a navigation view with nothing to guide along
 * would leave the user in a screen they cannot get a route into.
 */
function toDisplayMode(mode: string, navigating: boolean): DisplayMode {
  switch (mode) {
    case DisplayMode.NAVIGATION: return navigating ? DisplayMode.NAVIGATION : DisplayMode.ROUTE;
    case DisplayMode.ROUTE: return DisplayMode.ROUTE;
    case DisplayMode.SHOW_ON_MAP: return DisplayMode.SHOW_ON_MAP;
    default: return DisplayMode.RANGE;
  }
}
